import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { rentalAPI, FY_TONE } from '../lib/rentalRegisterAPI';
import { PropertyTable } from '../components/rental/PropertyTable';
import { HelpTip } from '../components/portal/HelpTip';
import { NewPropertyDialog } from '../components/rental/ClientRentalTab';

const STATS = [['total', 'Properties'], ['current_year', 'Current-year records'], ['awaiting_client', 'Awaiting client'], ['documents_received', 'Docs received'],
  ['accountant_review', 'Accountant review'], ['ready_for_tax_return', 'Ready for tax return'], ['sold_this_year', 'Sold this year'], ['missing_depreciation', 'Missing depreciation'], ['review_actions_required', 'Review actions required']];

const Staff = ({ meta }) => {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [f, setF] = useState({ q: '', status: '', property_type: '', fy_status: '' });
  const load = useCallback(() => rentalAPI.list(f).then(setRows).catch(() => setRows([])), [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { rentalAPI.dashboard().then(setStats).catch(() => {}); }, []);
  const sel = (k, opts, label) => <select className="border rounded h-9 px-2 text-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`rental-filter-${k}`}>
    <option value="">{label}</option>{(opts || []).map((o) => <option key={o}>{o}</option>)}</select>;
  return (
    <>
      <div className="grid grid-cols-4 lg:grid-cols-8 gap-2" data-testid="rental-stats">{STATS.map(([k, l]) => (
        <div key={k} className="rounded-md border px-3 py-2"><div className="text-lg font-semibold" data-testid={`rental-stat-${k}`}>{stats?.[k] ?? '—'}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>))}</div>
      <div className="flex flex-wrap gap-2">
        <Input className="w-72" placeholder="Search address, client, owner, manager, lender, document, RP id" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="rental-search" />
        {sel('status', meta?.rental_status, 'Any rental status')}{sel('property_type', meta?.property_types, 'Any type')}{sel('fy_status', meta?.fy_status, 'Any FY status')}
      </div>
      <PropertyTable rows={rows} showClients />
    </>
  );
};

const Client = ({ meta }) => {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const load = () => rentalAPI.list().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex justify-end"><Button size="sm" onClick={() => setAdding(true)} data-testid="my-rental-add"><Plus className="w-3.5 h-3.5 mr-1" />Add a property</Button></div>
      {rows.length === 0 && <p className="text-sm text-muted-foreground" data-testid="my-rental-empty">No rental properties yet.</p>}
      <div className="grid md:grid-cols-2 gap-3">{rows.map((r) => (
        <Card key={r.id} className="cursor-pointer hover:border-foreground/30" onClick={() => navigate(`/rental-properties/${r.id}`)} data-testid={`my-rental-${r.id}`}>
          <CardContent className="p-3 space-y-1 text-sm">
            <div className="font-medium">{r.address}</div>
            <div className="text-xs text-muted-foreground">{r.property_code} · {r.rental_status} · your share {r.my_percentage ?? '—'}%</div>
            {r.current_fy && <div className="flex items-center gap-2"><Badge className={FY_TONE[r.current_fy.status] || ''}>{r.current_fy.financial_year}: {r.current_fy.status}</Badge>
              <Button size="sm" variant="outline" className="h-7" onClick={(e) => { e.stopPropagation(); navigate(`/rental-properties/fy/${r.current_fy.id}`); }} data-testid={`my-rental-fy-${r.id}`}>Open year</Button></div>}
            {r.open_requests > 0 && <div className="text-xs text-amber-700">Action required: {r.open_requests} request(s)</div>}
          </CardContent></Card>))}</div>
      <NewPropertyDialog open={adding} onClose={() => setAdding(false)} meta={meta} onDone={load} />
    </>
  );
};

export default function RentalProperties() {
  const [meta, setMeta] = useState(null);
  useEffect(() => { rentalAPI.meta().then(setMeta).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="rental-page">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><Building2 className="w-6 h-6" />{meta?.me?.is_client ? 'My Rental Properties' : 'Rental Properties'}</h1>
      {meta?.me?.is_client && <p className="text-sm text-muted-foreground"><HelpTip term="depreciation">Depreciation</HelpTip> · <HelpTip term="FY">Financial year</HelpTip></p>}
      {meta && (meta.me.is_client ? <Client meta={meta} /> : <Staff meta={meta} />)}
    </div>
  );
}
